import { CustomOverlay, DrawnOverlay, FeatureType, ImageUpload } from '../../../types';
import { LitElement, css, html } from 'lit';
import { customElement, query, state } from 'lit/decorators';

import { RangeSliderComponent } from '../../range-slider-component';
import { StateController } from '../../../helpers/state/state-management/state-controller';
import accountInfo from '../../../helpers/state/account-info';
import { createTopicRequestFromTopic } from '../../../helpers/api/topics-api';
import featuresInfo from '../../../helpers/state/features-info';
import { getImageDimensions } from '../../../helpers/utils';
import pointCache from '../../../helpers/state/point-cache';
import topicData from '../../../helpers/state/topic-data';

@customElement('map-overlay-editor')
export class MapOverlayEditor extends LitElement {
    amplitude_callsite: string = 'Custom Overlay';

	private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindAccountState = new StateController(this, accountInfo);

    @query('#opacityControl')
    opacityControl?: RangeSliderComponent;

    @query('#rotationControl')
    rotationControl?: RangeSliderComponent;

    @state()
    drawnOverlays: DrawnOverlay[] = [];

    @state()
    uploadNewOverlay: boolean = false;

    @state()
    rotatePreviewCSS: string = '';

    @state()
    overlayEditCircle: google.maps.Circle | undefined;

    @state()
    image_url: string = '';

    @state()
    image_to_upload: ImageUpload | undefined;

    @state()
    currently_editing_overlay: DrawnOverlay | undefined;

    private async loadMapOverlay(overlay: CustomOverlay) {
        class EditableCustomOverlayView extends google.maps.OverlayView {
            public div_: HTMLDivElement | null = null;
        }

        const overlayImage = new EditableCustomOverlayView();
        const imageBounds = await getBoundsForOverlay(overlay);
        if (overlayImage === undefined || overlayImage === null) {
            return;
        }

        const overlayDiv = await createOverlayDiv(overlay);

        // Add the container when the overlay is added to the map.
        overlayImage.onAdd = function () {
            const div = overlayDiv;
            const panes = this.getPanes();
            if (panes !== undefined && panes !== null) {
                panes.overlayLayer.appendChild(div);
            }

            this.div_ = div;
        };


        overlayImage.draw = function () {
            if (this.div_ === null) {
                return;
            }
            const overlayProjection = this.getProjection();

            // Place the overlay on the map
            const sw = overlayProjection.fromLatLngToDivPixel(
                new google.maps.LatLng(imageBounds.south, imageBounds.west)
            );
            const ne = overlayProjection.fromLatLngToDivPixel(
                new google.maps.LatLng(imageBounds.north, imageBounds.east)
            );

            const panes = this.getPanes();
            if (panes === undefined || panes === null || ne === null || sw === null) {
                return;
            }
            const map_rotation = pointCache.map?.getHeading() ?? 0;

            // Calculate the center point
            const centerX = (sw.x + ne.x) / 2;
            const centerY = (sw.y + ne.y) / 2;

            // Calculate the width and height before rotation
            const width = ne.x - sw.x;
            const height = sw.y - ne.y;

            // Calculate the rotated corners
            const angle = (overlay.position.rotation - map_rotation) * (Math.PI / 180);
            const cosAngle = Math.cos(angle);
            const sinAngle = Math.sin(angle);

            const halfWidth = width / 2;
            const halfHeight = height / 2;

            const rotatedWidth = Math.abs(halfWidth * cosAngle - halfHeight * sinAngle) * 2;
            const rotatedHeight = Math.abs(halfWidth * sinAngle + halfHeight * cosAngle) * 2;

            // Adjust the div location based on the lat and lng values and the map rotation
            const div = this.div_ as HTMLElement;
            div.style.left = `${centerX - rotatedWidth / 2}px`;
            div.style.top = `${centerY - rotatedHeight / 2}px`;
            div.style.width = `${rotatedWidth}px`;
            div.style.height = `${rotatedHeight}px`;

            div.style.pointerEvents = 'auto';

            // Apply CSS transition for smooth rotation
            div.style.transition = 'transform 0.5s ease';

            // Rotate the image
            const rotation_value = overlay.position.rotation - map_rotation;
            div.style.transformOrigin = 'center center';
            div.style.transform = `rotate(${rotation_value}deg)`;
        };

        overlayImage.onRemove = function(){
            this.div_?.parentNode?.removeChild(this.div_);
        };

        if (pointCache.map === undefined) {
            return;
        }
        overlayImage.setMap(pointCache.map);

        // Set as currently editing overlay
        this.currently_editing_overlay = {
            google_overlay: overlayImage,
            position: overlay.position,
            image_url: overlay.image_url,
            _id: overlay._id ?? undefined,
            opacity: overlay.opacity,
        };
    }

    private async setOverlayEditbleById(id: string) {
        const overlay = this.drawnOverlays.find((o) => o._id?.$oid === id);
        if (overlay !== undefined) {
            // center the map on the overlay
            pointCache.map?.panTo(overlay.position.center);
            // fit bounds to the overlay
            this.currently_editing_overlay = overlay;
            await this.drawEditableCircle();
            this.currently_editing_overlay.google_overlay?.setMap(pointCache.map);
        }
    }

    private async setNewOverlayEditable() {
        const overlay = this.currently_editing_overlay;
        if (overlay !== undefined) {
            // center the map on the overlay
            pointCache.map?.panTo(overlay.position.center);
            // fit bounds to the overlay
            await this.drawEditableCircle();
        }
    }

    private async addEditableOverlay(imageurl: string) {
        const bounds = pointCache.map?.getBounds();
        if (bounds === undefined) {
            return;
        }
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        const radius = Math.abs(ne.lng() - sw.lng()) / 8;
        const center = bounds.getCenter();
        const overlay: CustomOverlay = {
            image_url: imageurl,
            opacity: 0.8,
            position: {
                center: {
                    lat: center.lat(),
                    lng: center.lng(),
                },
                radius,
                rotation: 0,
            },
            _id: undefined,
        };

        await this.loadMapOverlay(overlay);
        await this.setNewOverlayEditable();
    }

    private async adjustOverlayOpacity(opacity: number) {
        if (this.currently_editing_overlay !== undefined) {
            this.currently_editing_overlay.opacity = opacity / 100;
            await this.recalculateOverlayPosition(this.currently_editing_overlay);
        }
    }

    private async adjustOverlayRotation(rotation: number) {
        if (this.currently_editing_overlay !== undefined) {
            this.currently_editing_overlay.position.rotation = rotation;
            await this.recalculateOverlayPosition(this.currently_editing_overlay);
        }
    }

    openUpgradeDrawerShapes() {
        this.dispatchEvent(
            new CustomEvent('open-upgrade-dialog', {
                detail: {
                    featureType: FeatureType.CustomShapes
                },
            })
        );
    }

    private async drawEditableCircle() {
        if (this.currently_editing_overlay === undefined) {
            return;
        }
        const circle = new google.maps.Circle({
            center: this.currently_editing_overlay.position.center,
            radius: this.currently_editing_overlay.position.radius * 100000,
            map: pointCache.map,
            strokeColor: 'black',
            strokeOpacity: 0.8,
            strokeWeight: 2,
            editable: true,
            draggable: true,
            fillColor: 'transparent',
            fillOpacity: 0,
        },
        );

        this.overlayEditCircle = circle;

        await this.recalculateOverlayPosition(this.currently_editing_overlay);

        google.maps.event.addListener(this.overlayEditCircle, 'center_changed', async () => {
            if (this.currently_editing_overlay === undefined) {
                return;
            }
            this.currently_editing_overlay.position.center = {
                lat: circle.getCenter()?.lat() ?? 0,
                lng: circle.getCenter()?.lng() ?? 0
            };
            await this.recalculateOverlayPosition(this.currently_editing_overlay);
        });

        google.maps.event.addListener(this.overlayEditCircle, 'radius_changed', async () => {
            if (this.currently_editing_overlay === undefined) {
                return;
            }
            this.currently_editing_overlay.position.radius = circle.getRadius() / 100000;
            await this.recalculateOverlayPosition(this.currently_editing_overlay);
        });
    }

    private async recalculateOverlayPosition(overlay: DrawnOverlay) {
        if (this.overlayEditCircle === undefined) {
            return;
        }
        class EditableCustomOverlayView extends google.maps.OverlayView {
            public div_: HTMLDivElement | null = null;
        }
        const google_overlay = overlay.google_overlay as EditableCustomOverlayView;
        const imageBounds = await getBoundsForOverlay(overlay);
        const overlayDiv = await createOverlayDiv(overlay);
        overlayDiv.style.display = 'flex';

        google_overlay.onAdd = function () {
            const div = overlayDiv;

            const panes = this.getPanes();
            if (panes !== undefined && panes !== null) {
                panes.overlayLayer.appendChild(div);
            }

            this.div_ = div;
        };


        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        google_overlay.draw = async function () {
            if (this.div_ === null) {
                return;
            }
            const overlayProjection = this.getProjection();
            const sw = overlayProjection.fromLatLngToDivPixel(
                new google.maps.LatLng(imageBounds.south, imageBounds.west)
            );
            const ne = overlayProjection.fromLatLngToDivPixel(
                new google.maps.LatLng(imageBounds.north, imageBounds.east)
            );

            const panes = this.getPanes();
            if (panes === undefined || panes === null || ne === null || sw === null) {
                return;
            }
            const div = this.div_ as HTMLElement;

            const map_rotation = pointCache.map?.getHeading() ?? 0;

            // Calculate the center point
            const centerX = (sw.x + ne.x) / 2;
            const centerY = (sw.y + ne.y) / 2;

            // Calculate the width and height before rotation
            const width = ne.x - sw.x;
            const height = sw.y - ne.y;

            // Calculate the rotated corners
            const angle = (overlay.position.rotation - map_rotation) * (Math.PI / 180);
            const cosAngle = Math.cos(angle);
            const sinAngle = Math.sin(angle);

            const halfWidth = width / 2;
            const halfHeight = height / 2;

            const rotatedWidth = Math.abs(halfWidth * cosAngle - halfHeight * sinAngle) * 2;
            const rotatedHeight = Math.abs(halfWidth * sinAngle + halfHeight * cosAngle) * 2;

            // Adjust the div location based on the lat and lng values and the map rotation
            div.style.left = `${centerX - rotatedWidth / 2}px`;
            div.style.top = `${centerY - rotatedHeight / 2}px`;
            div.style.width = `${rotatedWidth}px`;
            div.style.height = `${rotatedHeight}px`;

            div.style.pointerEvents = 'auto';

            // Apply CSS transition for smooth rotation
            div.style.transition = 'transform 0.5s ease';

            // Rotate the image
            const rotation_value = overlay.position.rotation - map_rotation;
            div.style.transformOrigin = 'center center';
            div.style.transform = `rotate(${rotation_value}deg)`;

            div.style.opacity = overlay.opacity.toString();

        };
        if (pointCache.map === undefined) {
            return;
        }

        google_overlay.draw();
        if (google_overlay.div_ !== null) {
            google_overlay.div_.style.display = 'flex';
        }
    }

    private async saveUpdatedOverlay() {
        if (this.currently_editing_overlay === undefined) {
            return;
        }
        const custom_overlay: CustomOverlay = {
            _id: this.currently_editing_overlay._id,
            image_url: this.currently_editing_overlay.image_url,
            opacity: this.currently_editing_overlay.opacity,
            position: this.currently_editing_overlay.position,
        };
        const createTopicRequest = createTopicRequestFromTopic(topicData.topic);
        // Ensure custom_overlays is defined
        createTopicRequest.custom_overlays = createTopicRequest.custom_overlays ?? [];
        // find overlay in createTopicRequest and update it, if it doesn't exist, add it
        const index = createTopicRequest.custom_overlays.findIndex((o) => o._id === custom_overlay._id);
        if (index > -1) {
            createTopicRequest.custom_overlays[index] = custom_overlay;
        } else {
            createTopicRequest.custom_overlays.push(custom_overlay);
        }
        // update the topic
        await topicData.fetchAndUpdateTopicData(createTopicRequest, this.amplitude_callsite);

        this.refreshDrawnOverlays();

        // remove the editable overlay
        await this.cancelEditingOverlay();
    }

    private async cancelEditingOverlay() {
        this.refreshDrawnOverlays();
        this.currently_editing_overlay?.google_overlay?.setMap(null);
        this.currently_editing_overlay = undefined;
        this.overlayEditCircle?.setMap(null);
        this.overlayEditCircle = undefined;
        this.uploadNewOverlay = false;
    }

    private async removeOverlayById(id: string) {
        const overlay = this.drawnOverlays.find((o) => o._id?.$oid === id);
        const google_overlay = overlay?.google_overlay as google.maps.OverlayView;
        if (google_overlay !== undefined) {
            // update topic
            const createTopicRequest = createTopicRequestFromTopic(topicData.topic);
            // remove from topic
            const CustomOverlays = createTopicRequest.custom_overlays ?? [];
            createTopicRequest.custom_overlays = CustomOverlays.filter((o) => o._id?.$oid !== id);
            await topicData.fetchAndUpdateTopicData(createTopicRequest, this.amplitude_callsite);
            // ADD DISPACH TO UPDATE DRAWN
            this.refreshDrawnOverlays();
        }
    }

    private refreshDrawnOverlays() {
        // remove all overlays
        this.drawnOverlays = [];
        const google_overlay = this.currently_editing_overlay?.google_overlay as google.maps.OverlayView;
        if (google_overlay !== undefined) {
            google_overlay.setMap(null);
        }
        this.dispatchEvent( new CustomEvent('refresh-overlays' ));
        this.requestUpdate();
    }

    async onUpload(e: CustomEvent) {
        this.image_url = e.detail?.imageUrl ?? '';
        await this.addEditableOverlay(this.image_url);
    }

    async onEditOverlay(id: string) {
        await this.setOverlayEditbleById(id);
    }

    async onDuplicateOverlay(id: string) {
        const source = topicData.topic?.custom_overlays?.find((o) => o._id?.$oid === id);
        if (source === undefined) return;

        // Small offset so the copy doesn't sit exactly on the original
        const offset = 0.002;
        const duplicate: CustomOverlay = {
            image_url: source.image_url,
            opacity: source.opacity,
            position: {
                center: {
                    lat: source.position.center.lat + offset,
                    lng: source.position.center.lng + offset,
                },
                radius: source.position.radius,
                rotation: source.position.rotation,
            },
            _id: undefined,   // <-- no _id means save() will insert, not update
        };

        await this.loadMapOverlay(duplicate);
        await this.setNewOverlayEditable();
        if (this.currently_editing_overlay) {
            pointCache.map?.panTo(this.currently_editing_overlay.position.center);
        }
    }

    async onRemoveOverlay(id: string) {
        // confirm remove
        !confirm('Are you sure you want to remove this overlay?') ||
        await this.removeOverlayById(id);
    }

    async onChangeImage(e: CustomEvent) {
        this.image_to_upload = e.detail?.imageToUpload as ImageUpload;
        this.rotatePreviewCSS =  e.detail?.rotatePreviewCSS;
    }

    render() {
        const hasEntitlement = featuresInfo.customShapesEnabled || (topicData.topic?.custom_overlays?.length ?? 0) <= 1;
        return html`
            ${this.currently_editing_overlay === undefined ? html`
                <div class="modalContent">
                        <div slot="title" class="modalTitle">
                            <p class="info-paragraph">
                                Add or edit image overlays to your map. Overlays are images that are displayed on top of the map. Not seeing your overlays? Try refreshing the page after adding them.
                            </p>
                        </div>
                        ${hasEntitlement ? html`
                            <form-button @click="${() => {this.uploadNewOverlay = !this.uploadNewOverlay;}}">${this.uploadNewOverlay ? 'Cancel' : 'Upload New Overlay'}</form-button>
                        ` : html`
                            <form-button @click="${this.openUpgradeDrawerShapes}">Upgrade to add more overlays</form-button>
                        `}
                        <br /><br />
                        <div style="display: flex; flex-direction: row; justify-content: space-between; align-items: center ${hasEntitlement ? '' : 'disabled'}" @click="${hasEntitlement ? '' : this.openUpgradeDrawerShapes}">
                            <h4>Existing Overlays</h4>
                        </div>
                        <div class="modalBody">
                        ${!this.uploadNewOverlay ? html`
                            ${topicData.topic?.custom_overlays?.map((overlay) => html`
                                <div class="edit_row">
                                    <img class="edit_row__image" src="${overlay.image_url}" />
                                    <div class="edit_row__actions">
                                        <form-button variant="outline" size="xs" @click="${async () => await this.onEditOverlay(overlay._id?.$oid ?? '')}">Edit</form-button>
                                        <form-button variant="outline" size="xs" @click="${async () => await this.onDuplicateOverlay(overlay._id?.$oid ?? '')}" icon="content_copy"></form-button>
                                        <form-button variant="outline" size="xs" @click="${async () => await this.onRemoveOverlay(overlay._id?.$oid ?? '')}" icon="delete"></form-button>
                                    </div>
                                </div>
                            `)}
                        ` : html`
                            <image-upload-component
                                autoUpload
                                .imageUrl="${topicData.topic?.header_logo ?? ''}"
                                @changed-image-url="${this.onChangeImage}"
                                @retreived-autoupload-url="${this.onUpload}"
                                @on-change-image="${this.onChangeImage}"
                            ></image-upload-component>
                        `}
                        </div>
                        ${this.uploadNewOverlay && this.image_to_upload ? html`
                            <div class="modalFooter">
                                <form-button @click="${this.onUpload}">Upload Image</form-button>
                            </div>
                        ` : ''}

                </div>
            ` : html`
                <div class="modalContent">
                    <img src="${this.currently_editing_overlay.image_url}" style="${this.rotatePreviewCSS}; object-fit: contain; height:100px;" />
                    <div slot="title" class="modalTitle">
                        <p class="info-paragraph">
                            Edit the position and rotation of this overlay.
                        </p>
                    </div>
                    <div class="modalBody">
                        <b>Rotation</b>
                        <range-slider-component
                            id="rotationControl"
                            min=${0}
                            max=${360}
                            step=${1}
                            unit=${'°'}
                            @change=${async () => await this.adjustOverlayRotation(this.rotationControl?.value ?? 0)}
                            .value=${this.currently_editing_overlay.position.rotation}
                        ></range-slider-component>
                        <br>
                        <b>Opacity</b>
                        <range-slider-component
                            id="opacityControl"
                            min=${0}
                            max=${100}
                            step=${1}
                            unit=${'%'}
                            @change=${async () => await this.adjustOverlayOpacity(this.opacityControl?.value ?? 0)}
                            .value=${this.currently_editing_overlay.opacity * 100}
                        ></range-slider-component>
                    </div>
                    <div class="modalFooter">
                        <form-button variant="outline" @click="${this.cancelEditingOverlay}" size="sm">Cancel Edit</form-button>
                        <form-button id="saveChangesButton" @click="${async () => await this.saveUpdatedOverlay()}" size="sm">Save Changes</form-button>
                    </div>
                </div>
            `}
		`;
    }

    static get styles() {
        return css`
            :host {
                display: flex;
                flex-direction: column;
            }

            h4 {
                margin: 5px 0;
            }

            .exit-button {
                position: absolute;
                top: 1rem;
                right: 0;
                padding: 1rem;
                cursor: pointer;
            }

            .edit_row__actions {
                display: flex;
                flex-direction: row;
                gap: 10px;
            }

		    h4 {
                display: flex;
                align-items: center;
            }

            .input {
                width: 100%;
                padding: auto 5px;
            }

            .edit_row {
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 10px;
                margin: 10px 0;
            }

            .edit_row__image {
                width: 40px;
                height: 40;
            }

            .gatedField {
                display: flex;
                flex-direction: column;
                cursor: pointer;
            }

            .menuPng {
                margin-left: 0.25rem;
                height: 20px;
            }

            .modalContent {
                --mdc-icon-size: 18px;
                display: flex;
                flex-direction: column;
                text-align: left;
                background-color: white;
                max-height: 100%;
            }

            .title {
                margin: 0;
                padding: 0;
            }

            .disabled {
                opacity: 0.5;
            }

            .modalContent.disabled {
                pointer-events: none;
            }


            .modalTitle {
                text-align: left;
                margin-bottom: 1rem;
                margin-top: 1rem;
            }

            .modalBody {
                flex: 1;
                overflow-y: auto;
                overflow-x: hidden;
            }

            .modalBody::-webkit-scrollbar {
                width: 5px;
            }

            .modalBody::-webkit-scrollbar-track {
                background: var(--theme-background-color);
            }

            .modalBody::-webkit-scrollbar-thumb {
                background: var(--theme-border-color);
            }

            .modalFooter {
                display: flex;
                flex-direction: row;
                justify-content: flex-end;
                color: white;
                padding: 5px;
                gap: 10px;
            }

            .info-paragraph {
                color: var(--theme-input-color);
                margin-top: 0;
                margin-bottom: 0;
                font-size: 0.8rem;
                line-height: 120%;
                margin-top: 0.5rem;
                display: inline-flex;
                align-items: center;
            }

            .header-logo-label {
                margin: 10px 0 0;
            }
        `;
    }


}

/// Helper functions for elsewhere
export async function getBoundsForOverlay(overlay: CustomOverlay){
    const dimensions = async (image_url: string) => {
        return await getImageDimensions(image_url);
    };
    const dim = await dimensions(overlay.image_url);
    const latRad = overlay.position.center.lat * Math.PI / 180;
    const mercatorScaleFactor = 1 / Math.cos(latRad);

    // Calculate separate scaling factors for the width and height
    const scaleFactorWidth = (2 * overlay.position.radius) / dim.width;
    const scaleFactorHeight = (2 * overlay.position.radius) / dim.height;

    const scaledWidth = dim.width * scaleFactorWidth * mercatorScaleFactor;
    const scaledHeight = dim.height * scaleFactorHeight * mercatorScaleFactor;

    return {
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        north: overlay.position.center.lat + scaledHeight / 2,
        south: overlay.position.center.lat - scaledHeight / 2,
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        east: overlay.position.center.lng + scaledWidth / 2,
        west: overlay.position.center.lng - scaledWidth / 2,
    };
}

export async function createOverlayDiv(overlay: CustomOverlay, show = true) {
    const div = document.createElement('div');
    div.style.borderStyle = 'none';
    div.style.borderWidth = '0px';
    div.style.position = 'absolute';
    div.style.display = 'flex';
    div.style.justifyContent = 'center';
    div.style.alignItems = 'center';
    div.style.width = '100%';
    div.style.height = '100%';

    // Create the img element and attach it to the div.
    const img = document.createElement('img');
    img.src = overlay.image_url;
    img.style.width = '100%';
    img.style.height = 'auto';
    img.style.opacity = overlay.opacity.toString();

    if (!show) {
        div.style.display = 'none';
    }

    div.appendChild(img);
    return div;
}


